import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Typography from '@mui/material/Typography';
import { PermissionDecorator } from '@highimpact/react-decorators';
import { Tooltip } from '@mui/material';

// ************** Types and Interfaces **************
export type NavigationUrlIcon = {
    type: 'url' | 'base64';
    src: string;
    alt?: string;
    width?: number;
    height?: number;
    className?: string;
    style?: React.CSSProperties;
};

export type NavigationIcon = React.ReactNode | NavigationUrlIcon;

export type NavigationItem = {
    id: string;
    label: string;
    labelStyle?: React.CSSProperties;
    tooltip?: string;
    tooltipStyle?: React.CSSProperties;
    icon?: NavigationIcon;
    to: string;
    isExternal?: boolean;
    disabled?: boolean;
    target?: '_self' | '_blank' | '_parent' | '_top';
    rel?: string;
};

export type NavigationProps = {
    items: NavigationItem[];
    defaultActiveId?: string;
    activeId?: string;
    width?: number | string;
    showLabels?: boolean;
    usePermissionDecorator?: boolean;
    onNavigate?: (
        item: NavigationItem,
        event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    ) => void;
    onActiveChange?: (item: NavigationItem) => void;
};

// ******************* Helper Functions ******************

/**
 * Normalizes a pathname by removing trailing slashes, except for the root path.
 * @param pathname The pathname to normalize.
 * @returns The normalized pathname.
 */
function normalizePath(pathname: string): string {
    if (pathname === '/') {
        return '/';
    }

    return pathname.replace(/\/+$/, '');
}

/** Retrieves the current URL in a safe way, returning undefined if not in a browser environment.
 * @returns The current URL or undefined if not available.
 */
function getCurrentUrl(): URL | undefined {
    if (typeof window === 'undefined') {
        return undefined;
    }

    return new URL(window.location.href);
}

/** Determines the active navigation item ID based on the current URL and the provided navigation items.
 * It looks for the best match by comparing the current path with each item's URL, considering both exact and nested matches.
 * @param items The list of navigation items to evaluate.
 * @returns The ID of the best matching navigation item, or undefined if no match is found.
 */
function getAutoActiveId(items: NavigationItem[]): string | undefined {
    const currentUrl = getCurrentUrl();

    if (!currentUrl) {
        return undefined;
    }

    const currentPath = normalizePath(currentUrl.pathname);
    let bestMatch: { id: string; score: number } | undefined;

    for (const item of items) {
        let itemUrl: URL;

        try {
            itemUrl = new URL(item.to, currentUrl);
        } catch {
            continue;
        }

        if (itemUrl.origin !== currentUrl.origin) {
            continue;
        }

        const itemPath = normalizePath(itemUrl.pathname);
        const isExactMatch = currentPath === itemPath;
        const isNestedMatch =
            itemPath !== '/' && currentPath.startsWith(`${itemPath}/`);

        if (!isExactMatch && !isNestedMatch) {
            continue;
        }

        const score = itemPath.length;

        if (!bestMatch || score > bestMatch.score) {
            bestMatch = { id: item.id, score };
        }
    }

    return bestMatch?.id;
}

/**
 * Determines the appropriate `rel` attribute for a navigation item.
 * @param item The navigation item to evaluate.
 * @returns The `rel` attribute value or undefined if not applicable.
 */
function getSafeRel(item: NavigationItem): string | undefined {
    if (item.rel) {
        return item.rel;
    }

    if ((item.isExternal || item.target === '_blank') && item.target !== '_self') {
        return 'noopener noreferrer';
    }

    return undefined;
}

/** Type guard to check if a given icon is a NavigationUrlIcon.
 * @param icon The icon to check.
 * @returns True if the icon is a NavigationUrlIcon, false otherwise.
 */
function isNavigationUrlIcon(icon: NavigationIcon | undefined): icon is NavigationUrlIcon {
    if (!icon || typeof icon !== 'object') {
        return false;
    }

    return (
        'type' in icon &&
        (icon.type === 'url' || icon.type === 'base64') &&
        'src' in icon &&
        typeof icon.src === 'string'
    );
}

/** Renders a navigation icon based on the provided NavigationIcon type.
 * If the icon is a URL icon, it renders an <img> element with the specified properties.
 * If the icon is a React node, it renders it directly.
 * @param icon The NavigationIcon to render.
 * @returns A React node representing the rendered icon, or undefined if no icon is provided.
 */
function renderNavigationIcon(
    icon: NavigationIcon | undefined,
    slotSize: number,
): React.ReactNode {
    if (!icon) {
        return undefined;
    }

    const content = isNavigationUrlIcon(icon) ? (
        <img
            src={icon.src}
            alt={icon.alt ?? ''}
            width={icon.width ?? 22}
            height={icon.height ?? 22}
            className={icon.className}
            style={{
                objectFit: 'contain',
                display: 'block',
                flexShrink: 0,
                ...icon.style,
            }}
        />
    ) : (
        icon
    );

    return (
        <Box
            component="span"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: slotSize,
                height: slotSize,
                flexShrink: 0,
            }}
        >
            {content}
        </Box>
    );
}

// ******************* Main Component ******************

/**
 * MUI Navigation component.
 * It supports both internal and external links, automatic active state based on the current URL, and custom icons.
 * @param items The list of navigation items to display.
 * @param defaultActiveId The ID of the navigation item to be active by default (if activeId is not provided).
 * @param activeId The ID of the currently active navigation item (controlled mode).
 * @param width The width of the navigation component.
 * @param showLabels Whether to show labels for the navigation items.
 * @param usePermissionDecorator Whether to wrap navigation items with the PermissionDecorator for access control.
 * @param onNavigate Callback function triggered when a navigation item is clicked.
 * @param onActiveChange Callback function triggered when the active navigation item changes.
 * @returns A React component rendering the navigation bar.
 */
export default function HINavigation({
    items,
    defaultActiveId,
    activeId,
    width = 'auto',
    showLabels = true,
    usePermissionDecorator = false,
    onNavigate,
    onActiveChange,
}: NavigationProps) {
    const firstItemId = items[0]?.id;
    const autoActiveId = getAutoActiveId(items);

    const [internalActiveId, setInternalActiveId] = useState<string | undefined>(
        defaultActiveId ?? autoActiveId ?? firstItemId,
    );

    const onClickItem = (item: NavigationItem, event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (item.disabled) {
            event.preventDefault();
            return;
        }

        if (!/^https?:\/\//i.test(item.to) && onNavigate) {
            event.preventDefault();
        }

        if (!activeId) {
            setInternalActiveId(item.id);
        }

        onActiveChange?.(item);
        onNavigate?.(item, event);
    };

    useEffect(() => {
        if (activeId) {
            return;
        }

        if (autoActiveId && autoActiveId !== internalActiveId) {
            setInternalActiveId(autoActiveId);
            return;
        }

        if (!defaultActiveId && !internalActiveId && firstItemId) {
            setInternalActiveId(firstItemId);
        }
    }, [activeId, autoActiveId, defaultActiveId, firstItemId, internalActiveId]);

    const selectedId = activeId ?? internalActiveId;

    if (items.length === 0) {
        return <Typography variant="body2">No navigation items provided.</Typography>;
    }

    const iconSlotSize = Math.max(
        24,
        ...items.map((item) =>
            isNavigationUrlIcon(item.icon)
                ? Math.max(item.icon.width ?? 22, item.icon.height ?? 22)
                : 0,
        ),
    );
    const actionSx = {
        maxWidth: 'none',
        minWidth: 0,
        height: 'auto',
        alignSelf: 'stretch',
        padding: '8px 12px',
        justifyContent: 'center',
        ...(!showLabels && {
            '& .MuiBottomNavigationAction-label, & .MuiBottomNavigationAction-label.Mui-selected': {
                display: 'none',
            }
        })
    };

    const renderAction = (item: NavigationItem, withKey: boolean) => {
        const isExternalLink = item.isExternal || /^https?:\/\//i.test(item.to);

        const action = (
            <BottomNavigationAction
                key={withKey ? item.id : undefined}
                value={item.id}
                showLabel={showLabels}
                label={<Typography style={item.labelStyle}>{item.label}</Typography>}
                icon={renderNavigationIcon(item.icon, iconSlotSize)}
                disabled={item.disabled}
                component="a"
                href={item.to}
                target={item.target ?? (isExternalLink ? '_blank' : '_self')}
                rel={getSafeRel(item)}
                onClick={(event: React.MouseEvent<HTMLAnchorElement>) => onClickItem(item, event)}
                sx={actionSx}
            />
        );

        if (!item.tooltip) {
            return action;
        }

        return (
            <Tooltip
                key={withKey ? item.id : undefined}
                title={item.tooltip}
                arrow
                slotProps={{ tooltip: { style: item.tooltipStyle } }}
            >
                <span style={{ display: 'flex', flex: 1, alignSelf: 'stretch' }}>
                    {action}
                </span>
            </Tooltip>
        );
    };
    return (
        <Box sx={{ width }} >
            <BottomNavigation showLabels={showLabels} value={selectedId ?? false} sx={{ height: "auto", alignItems: 'stretch' }}>
                {items.map((item) =>
                    usePermissionDecorator ? (
                        <PermissionDecorator key={item.id} item={item.id}>
                            {renderAction(item, false)}
                        </PermissionDecorator>
                    ) : (
                        renderAction(item, true)
                    ),
                )}
            </BottomNavigation>
        </Box>
    );
}
